import React from 'react';
import { motion } from 'framer-motion';
import {
  MapPin, Search, Heart, Camera, Bell, Route, Apple, CheckCircle2, ChevronRight,
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Section, Eyebrow, FadeIn } from '../components/ui-bits';
import Breadcrumb from '../components/Breadcrumb';

const LOGO = 'https://customer-assets.emergentagent.com/job_bringing-light/artifacts/7tgkxuxg_logo%20CIS.png';

const T = {
  es: {
    eyebrow: 'App · CIS',
    heroTitle: 'Encuentra a tus seres queridos,',
    heroTitleAccent: 'guarda su memoria.',
    heroSubtitle: 'CIS (Cementerio Israelita Santiago) es la app oficial para navegar el cementerio y ubicar sepulturas con precisión GPS. Recorre cuarteles, guarda visitas, deja una piedra virtual y honra la memoria de quienes ya no están.',
    contactCta: 'Escribir a info@tair.site',
    features: 'Características',
    featuresTitle: 'Diseñada con respeto y calma.',
    how: 'Cómo funciona',
    howTitle: 'De la búsqueda al recuerdo, en un gesto.',
    coverage: 'Servicios',
    coverageTitle: 'Todo lo que necesitas al visitar.',
    trust1: 'Con respeto',
    trust2: 'Sin publicidad',
    trust3: 'Datos protegidos',
  },
  en: {
    eyebrow: 'App · CIS',
    heroTitle: 'Find your loved ones,',
    heroTitleAccent: 'keep their memory.',
    heroSubtitle: 'CIS (Santiago Jewish Cemetery) is the official app to navigate the cemetery and locate graves with GPS precision. Walk through sections, log visits, leave a virtual stone and honour the memory of those who are no longer here.',
    contactCta: 'Write to info@tair.site',
    features: 'Features',
    featuresTitle: 'Designed with respect and calm.',
    how: 'How it works',
    howTitle: 'From search to remembrance in one gesture.',
    coverage: 'Services',
    coverageTitle: 'Everything you need when you visit.',
    trust1: 'With respect',
    trust2: 'No ads',
    trust3: 'Protected data',
  },
};

const CIS = () => {
  const { lang } = useLanguage();
  const s = T[lang] || T.es;

  const features = [
    { icon: Search, t: lang === 'en' ? 'Search by name' : 'Búsqueda por nombre', d: lang === 'en' ? 'Type the person\u2019s name and go straight to their grave.' : 'Escribe el nombre y llegas directo a la sepultura.' },
    { icon: MapPin, t: lang === 'en' ? 'GPS turn-by-turn' : 'Navegación GPS interna', d: lang === 'en' ? 'Guided route inside the cemetery with maps of all sections.' : 'Ruta guiada dentro del cementerio con mapas de todos los cuarteles.' },
    { icon: Heart, t: lang === 'en' ? 'Digital tribute' : 'Homenaje digital', d: lang === 'en' ? 'Leave a virtual stone, a candle or a personal note in memory.' : 'Deja una piedra virtual, una vela o una nota personal en memoria.' },
    { icon: Camera, t: lang === 'en' ? 'Photo memories' : 'Recuerdos con foto', d: lang === 'en' ? 'Save photos of your visits privately for you and your family.' : 'Guarda fotos de tus visitas de forma privada para ti y tu familia.' },
    { icon: Bell, t: lang === 'en' ? 'Yahrzeit reminders' : 'Recordatorios de Yahrzeit', d: lang === 'en' ? 'Get notified before every anniversary with the halachic date.' : 'Notificaciones antes de cada aniversario con fecha halájica.' },
    { icon: Route, t: lang === 'en' ? 'Family route' : 'Ruta familiar', d: lang === 'en' ? 'Build a single route through several graves to visit in one trip.' : 'Arma una ruta única para visitar varias sepulturas en un solo recorrido.' },
  ];

  const services = lang === 'en' ? [
    'Full name and date search across the cemetery database',
    'Aerial view with all sections and paths mapped',
    'Grave details: dates, section, path and additional notes',
    'Family tree: link relatives buried in the same cemetery',
    'Share the location with family members by link',
    'Contact the cemetery office directly from the app',
  ] : [
    'Búsqueda por nombre completo y fecha en toda la base',
    'Vista aérea con todos los cuarteles y senderos',
    'Detalles de sepultura: fechas, cuartel, calle y notas',
    'Árbol familiar: enlaza parientes sepultados en el mismo lugar',
    'Comparte la ubicación con familiares por link',
    'Contacto directo con la oficina del cementerio',
  ];

  const steps = lang === 'en' ? [
    { n: '01', t: 'Install CIS', d: 'Download from the App Store on your iPhone.' },
    { n: '02', t: 'Search the person', d: 'By full name, section or approximate date.' },
    { n: '03', t: 'Navigate and remember', d: 'Follow the GPS route and leave your tribute at the grave.' },
  ] : [
    { n: '01', t: 'Instala CIS', d: 'Descarga desde el App Store en tu iPhone.' },
    { n: '02', t: 'Busca a la persona', d: 'Por nombre completo, cuartel o fecha aproximada.' },
    { n: '03', t: 'Navega y recuerda', d: 'Sigue la ruta GPS y deja tu homenaje en la sepultura.' },
  ];

  return (
    <div data-testid="cis-marketing">
      <Breadcrumb items={[
        { to: '/que-hacemos', label: lang === 'en' ? 'What We Do' : 'Qué Hacemos' },
        { to: '/que-hacemos/desarrollo-software', label: lang === 'en' ? 'Software' : 'Software' },
        { to: null, label: 'CIS' },
      ]} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 light-beam" />
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 lg:px-12 pt-4 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <FadeIn><Eyebrow>{s.eyebrow}</Eyebrow></FadeIn>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                className="mt-8 font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
              >
                {s.heroTitle}
                <br />
                <span className="text-amber-300">{s.heroTitleAccent}</span>
              </motion.h1>
              <FadeIn delay={0.25}>
                <p className="mt-8 max-w-xl font-body text-base sm:text-lg text-white/65 leading-relaxed">{s.heroSubtitle}</p>
              </FadeIn>
              <FadeIn delay={0.35}>
                <div className="mt-10 flex flex-wrap gap-3">
                  <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer" data-testid="cis-appstore" className="inline-flex items-center gap-3 h-13 px-6 py-3 rounded-full bg-white text-[#0a1322] font-semibold text-sm hover:bg-amber-100 transition-colors">
                    <Apple className="w-5 h-5" />
                    <div className="flex flex-col items-start leading-tight">
                      <span className="text-[10px] uppercase tracking-widest text-black/60">{lang === 'en' ? 'Download on the' : 'Descargar en'}</span>
                      <span>App Store</span>
                    </div>
                  </a>
                  <a href="mailto:info@tair.site" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-transparent border border-white/20 text-white/85 font-medium text-sm hover:bg-white/5 hover:border-white/40 transition-colors">{s.contactCta}</a>
                </div>
              </FadeIn>
              <FadeIn delay={0.5}>
                <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/50">
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust1}</span>
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust2}</span>
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust3}</span>
                </div>
              </FadeIn>
            </div>
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <FadeIn delay={0.35}>
                <div className="relative">
                  <div className="absolute inset-0 blur-3xl bg-amber-300/25 rounded-full" />
                  <div className="relative w-56 h-56 md:w-72 md:h-72 rounded-[3rem] overflow-hidden shadow-2xl border border-white/10 bg-[#0a1322]">
                    <img src={LOGO} alt="CIS" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute -bottom-4 -right-4 text-[10px] uppercase tracking-widest text-white/40 bg-[#070d18]/80 border border-white/10 rounded-full px-3 py-1.5 backdrop-blur">iOS · iPhone · iPad</div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      <Section className="py-24">
        <FadeIn><Eyebrow>{s.features}</Eyebrow></FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl lg:text-5xl text-white max-w-3xl leading-[1.1]">{s.featuresTitle}</h2>
        </FadeIn>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <FadeIn key={i} delay={i * 0.06}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover">
                  <div className="w-11 h-11 rounded-xl bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-300"><Icon className="w-5 h-5" /></div>
                  <h4 className="mt-6 font-display font-bold text-lg md:text-xl text-white tracking-tight">{f.t}</h4>
                  <p className="mt-3 font-body text-sm text-white/55 leading-relaxed">{f.d}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      <Section className="pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FadeIn className="lg:col-span-5">
            <Eyebrow>{s.coverage}</Eyebrow>
            <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl text-white leading-[1.1]">{s.coverageTitle}</h2>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-7">
            <ul className="space-y-3">
              {services.map((c, i) => (
                <li key={i} className="flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-5 card-hover">
                  <ChevronRight className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" />
                  <span className="text-white/80 font-body">{c}</span>
                </li>
              ))}
            </ul>
          </FadeIn>
        </div>
      </Section>

      <Section className="pb-32">
        <FadeIn><Eyebrow>{s.how}</Eyebrow></FadeIn>
        <FadeIn delay={0.08}>
          <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl lg:text-5xl text-white max-w-3xl leading-[1.1]">{s.howTitle}</h2>
        </FadeIn>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {steps.map((step, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover">
                <div className="font-display text-amber-300 text-sm tracking-widest">{step.n}</div>
                <h4 className="mt-4 font-display font-bold text-xl text-white tracking-tight">{step.t}</h4>
                <p className="mt-3 font-body text-white/60 leading-relaxed">{step.d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default CIS;
